import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Sparkle, MagicWand, TrendUp, ChartLine, FolderOpen, FilmStrip,
  Hash, Image as ImageIcon, ArrowRight, Star, Lightning, Check,
  Question, Crown, Trophy
} from "@phosphor-icons/react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";

const features = [
  { icon: MagicWand, title: "AI Script Studio", desc: "Generate hooks, body, CTA + viral score across 7 niches." },
  { icon: Hash, title: "Caption Engine", desc: "Platform-optimized captions, hashtags, SEO keywords." },
  { icon: ImageIcon, title: "Thumbnail AI", desc: "Bold YouTube-style thumbnails with viral color grading." },
  { icon: TrendUp, title: "Trend Radar", desc: "Live trending topics + viral audio across platforms." },
  { icon: ChartLine, title: "Analytics", desc: "Engagement prediction, viral score tracking, growth charts." },
  { icon: FolderOpen, title: "Creator Workspace", desc: "Projects, folders, drafts, calendar, export history." },
];

const niches = ["Gaming", "Finance", "Motivation", "AI", "Fitness", "Tech", "Storytelling"];

const faqs = [
  { q: "How does ClipForge AI generate viral content?", a: "We combine Claude Sonnet 4.5 with our proprietary viral scoring model trained on top-performing short-form videos to engineer hooks, scripts, and captions optimized for retention." },
  { q: "Which platforms are supported?", a: "TikTok, YouTube Shorts, and Instagram Reels. Each output is auto-formatted to platform-specific best practices." },
  { q: "Do I keep ownership of the content?", a: "Always. Everything you generate is 100% yours to use commercially." },
  { q: "Is there a free plan?", a: "Yes — 50 AI credits per month, unlimited project drafts, and full access to the trend radar." },
];

export default function Landing() {
  const [leaders, setLeaders] = useState([]);
  useEffect(() => {
    api.get("/referrals/leaderboard").then(r => setLeaders(r.data || [])).catch(() => setLeaders([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden grain">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-32 w-[600px] h-[600px] bg-[#FF3B6B]/15 blur-[120px] rounded-full" />
          <div className="absolute top-40 -right-32 w-[500px] h-[500px] bg-[#00E5FF]/8 blur-[100px] rounded-full" />
        </div>
        <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 glass px-3 py-1.5 rounded-full text-xs text-white/80 mb-6" data-testid="hero-badge">
              <Sparkle weight="fill" className="text-[#FF3B6B]" size={14} />
              Powered by Claude Sonnet 4.5 · Gemini Nano Banana
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05]">
              Forge <span className="text-gradient-coral">viral shorts</span><br />in 30 seconds flat.
            </h1>
            <p className="mt-6 text-lg text-white/60 max-w-xl">
              ClipForge AI is the command center for TikTok, Reels & Shorts creators. Generate scripts, captions, thumbnails, and trend-perfect content — backed by viral scores you can trust.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth?mode=signup" className="btn-primary px-6 py-3 rounded-lg font-semibold flex items-center gap-2" data-testid="hero-cta-primary">
                Start free <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/pricing" className="glass px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors" data-testid="hero-cta-secondary">
                View pricing
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-4 text-sm text-white/50">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-[#0A0A0A] bg-gradient-to-br from-[#FF3B6B] to-[#EAB308]" />
                ))}
              </div>
              <span><span className="text-white font-semibold">12,400+</span> creators forging</span>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} weight="fill" size={14} className="text-[#EAB308]" />)}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="lg:col-span-5">
            <div className="relative glass-strong rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#FF3B6B]" />
                  <span className="text-xs text-white/60 font-mono">LIVE PREVIEW</span>
                </div>
                <span className="text-xs text-white/40 font-mono">script.studio</span>
              </div>
              <div className="space-y-3">
                <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-xs text-white/40 mb-1">HOOK · viral score</p>
                  <p className="text-sm">"Wait... your phone is making you BROKE?"</p>
                  <div className="mt-2 font-mono text-xs text-[#FF3B6B]">94/100</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-xs text-white/40 mb-1">BODY</p>
                  <p className="text-sm text-white/80">[zoom] 80% of people don't know this... [cut] These 3 apps drain $400/month silently...</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-xs text-white/40 mb-1">HASHTAGS</p>
                  <p className="text-sm text-[#00E5FF] font-mono">#moneytips #fintok #savings #budget</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="text-white/50">Generated · 1.2s</span>
                <span className="text-white/70 flex items-center gap-1"><Lightning size={12} weight="fill" className="text-[#EAB308]" />3 credits</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Logos */}
        <div className="max-w-7xl mx-auto mt-20 opacity-50">
          <p className="text-center text-xs text-white/40 mb-4 tracking-widest uppercase">Trusted by creators worldwide</p>
          <Marquee gradient gradientColor="#0A0A0A" speed={40}>
            {["Hypebeast", "MrBeast Lab", "FinTok Daily", "GymShark", "CreatorOS", "Vidly", "ShortsRank"].map((b, i) => (
              <span key={i} className="mx-12 text-2xl font-bold tracking-tight text-white/60">{b}</span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="px-6 py-24 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12">
          <div className="md:col-span-7">
            <p className="text-[#FF3B6B] text-sm font-mono mb-3">// THE FORGE</p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter">Every tool a creator needs.<br /><span className="text-white/40">Zero busywork.</span></h2>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-white/60">Six AI-powered modules. One unified workspace. Built for creators who ship daily and obsess over retention.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className={`glass rounded-xl p-6 hover:-translate-y-1 hover:border-white/30 transition-all duration-300 ${i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 4 ? "md:col-span-5" : i === 5 ? "md:col-span-7" : "md:col-span-6"}`}
              data-testid={`feature-card-${i}`}
            >
              <f.icon weight="duotone" size={32} className="text-[#FF3B6B] mb-4" />
              <h3 className="text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-white/60 text-sm">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* NICHES */}
      <section className="px-6 py-20 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-[#FF3B6B] text-sm font-mono mb-3">// NICHES</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter">7 viral verticals, 1 dashboard</h2>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {niches.map((n) => (
            <span key={n} className="glass px-5 py-2.5 rounded-full text-sm hover:bg-[#FF3B6B] hover:text-white hover:border-[#FF3B6B] transition-all cursor-default" data-testid={`niche-${n.toLowerCase()}`}>
              {n}
            </span>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="px-6 py-24 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#FF3B6B] text-sm font-mono mb-3">// WORKFLOW</p>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter">From idea to upload in <span className="text-[#FF3B6B]">3 steps</span></h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Pick your niche", d: "Choose from gaming, finance, motivation, AI, fitness, tech, or storytelling." },
            { n: "02", t: "Generate the package", d: "Script + hook + caption + hashtags + thumbnail — all in one click." },
            { n: "03", t: "Export & post", d: "Vertical 9:16 ready. Copy captions. Download thumbnails. Go viral." },
          ].map((s) => (
            <div key={s.n} className="glass rounded-xl p-8" data-testid={`step-${s.n}`}>
              <div className="font-mono text-5xl text-[#FF3B6B]/30 mb-4">{s.n}</div>
              <h3 className="text-xl font-semibold mb-2">{s.t}</h3>
              <p className="text-white/60">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="px-6 py-24 max-w-7xl mx-auto">
        <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-center mb-16">Creators are <span className="text-gradient-coral">winning</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { name: "Maya R.", role: "FinTok · 480K", text: "Hit 3 million views in a week using the hook generator. Sounds fake. It's not." },
            { name: "Devon K.", role: "Gaming Shorts · 1.2M", text: "The viral score caught a flop hook before I posted. Saved me from a dead video." },
            { name: "Aisha P.", role: "Wellness · 220K", text: "I batch a month of content in one afternoon now. Genuinely changed my workflow." },
          ].map((t, i) => (
            <div key={i} className="glass rounded-xl p-6" data-testid={`testimonial-${i}`}>
              <div className="flex gap-0.5 mb-3">{[1, 2, 3, 4, 5].map(i => <Star key={i} weight="fill" size={14} className="text-[#EAB308]" />)}</div>
              <p className="text-white/80 mb-4">"{t.text}"</p>
              <div>
                <p className="text-sm font-semibold">{t.name}</p>
                <p className="text-xs text-white/50">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CREATOR LEADERBOARD */}
      <section id="leaderboard" className="px-6 py-24 max-w-5xl mx-auto" data-testid="leaderboard-section">
        <div className="text-center mb-12">
          <p className="text-[#FF3B6B] text-sm font-mono mb-3">// AMBASSADORS</p>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter">Top creators earning <span className="text-[#FF3B6B]">30%</span></h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">Refer ClipForge to your audience. Earn recurring commission on every paid signup, paid out via Razorpay.</p>
        </div>
        <div className="glass-strong rounded-2xl overflow-hidden">
          {leaders.length === 0 ? (
            <div className="p-10 text-center">
              <Trophy size={48} weight="duotone" className="mx-auto mb-3 text-[#FF3B6B]/50" />
              <p className="text-white/70 font-medium">Leaderboard is warming up.</p>
              <p className="text-white/50 text-sm mt-1">Be the first creator on the wall — share your link the moment you sign up.</p>
              <Link to="/auth?mode=signup" className="inline-flex items-center gap-2 mt-5 btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold" data-testid="leaderboard-join-cta">
                Join the program <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          ) : (
            <>
              {leaders.slice(0, 5).map((b, i) => (
                <div key={i} className="flex items-center gap-4 p-4 border-b border-white/5 last:border-none" data-testid={`landing-leader-${i}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? "bg-[#EAB308] text-black"
                    : i === 1 ? "bg-white/30"
                    : i === 2 ? "bg-[#FF3B6B]/40"
                    : "bg-white/10"
                  }`}>
                    {i === 0 ? <Crown size={16} weight="fill" /> : i + 1}
                  </div>
                  {b.picture
                    ? <img src={b.picture} alt="" className="w-10 h-10 rounded-full" />
                    : <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF3B6B] to-[#EAB308]" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{b.name}</p>
                    <p className="text-xs text-white/50">{b.paid_signups} paid referral{b.paid_signups === 1 ? "" : "s"}</p>
                  </div>
                  <div className="font-mono text-sm text-[#FF3B6B] font-bold">₹{b.total_commission.toLocaleString("en-IN")}</div>
                </div>
              ))}
              <div className="p-4 text-center border-t border-white/5">
                <Link to="/auth?mode=signup" className="text-sm text-[#FF3B6B] hover:underline inline-flex items-center gap-1" data-testid="leaderboard-cta">
                  Get your link <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* PRICING TEASE */}
      <section className="px-6 py-24 max-w-7xl mx-auto">
        <div className="glass-strong rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-[#FF3B6B]/20 blur-[120px] rounded-full pointer-events-none" />
          <div className="relative">
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter mb-4">Start free. Scale when you blow up.</h2>
            <p className="text-white/60 max-w-xl mx-auto mb-8">50 AI credits/month free, forever. Upgrade only when your channel demands it.</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              {["No credit card", "Cancel anytime", "Unlimited drafts"].map(b => (
                <span key={b} className="flex items-center gap-2 text-white/70"><Check weight="bold" className="text-[#FF3B6B]" /> {b}</span>
              ))}
            </div>
            <Link to="/pricing" className="inline-block mt-8 btn-primary px-8 py-3 rounded-lg font-semibold" data-testid="pricing-cta">
              See plans
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 py-24 max-w-3xl mx-auto">
        <h2 className="text-4xl font-bold tracking-tighter text-center mb-12">Common questions</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="glass rounded-xl p-5 group" data-testid={`faq-${i}`}>
              <summary className="flex justify-between items-center cursor-pointer">
                <span className="font-medium flex items-center gap-3"><Question className="text-[#FF3B6B]" size={18} /> {f.q}</span>
                <span className="text-[#FF3B6B] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-white/60 text-sm pl-7">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
